import { router } from "@inertiajs/react"
import { CheckCheck, MailQuestion } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { CandidateReviewCard } from "@/components/candidates/candidate-review-card"
import { EmptyState } from "@/components/empty-state"
import { HomeTriageSection } from "@/components/home-triage/home-triage-section"
import type { CandidatesToReviewSection as SectionData } from "@/types/home-triage"

/**
 * OKS-953 (PRD R6) — Agency/Home § « Invitations en veille ».
 *
 * The agency consumer of the shipped shell + card. Three deltas from its brand
 * twin, and nothing else:
 *
 *   1. it reloads the AGENCY prop key `agency_invites_quiet` on Retry;
 *   2. its two ZERO-ROW descriptions use the `agency.*` variants — the only
 *      strings in the block that assert a single brand. Titles, subtitles,
 *      cues and sub-states are REUSED verbatim (UX Spec §9 / PRD OQ-G):
 *      minting full agency copy blocks would be six screens of duplication
 *      for no comprehension gain;
 *   3. each card carries a `brand` badge, supplied per item by the agency
 *      serializer — the shared card renders it automatically, so there is
 *      nothing to do here.
 *
 * ⚠️ Cold start is PORTFOLIO-level, not per-brand (UX Spec §3): "no assigned
 * brand has ever had one". The server owns that distinction; this component
 * only renders it.
 */
export interface AgencyInvitesQuietSectionProps {
  section: SectionData
}

export function AgencyInvitesQuietSection({
  section,
}: AgencyInvitesQuietSectionProps) {
  const { t } = useTranslation()
  const [isRetrying, setIsRetrying] = useState(false)

  function handleRetry() {
    setIsRetrying(true)
    router.reload({
      only: ["agency_invites_quiet"],
      onFinish: () => setIsRetrying(false),
    })
  }

  return (
    <HomeTriageSection
      title={t("candidates.home_invite_stale.title")}
      subtitle={t("candidates.home_invite_stale.subtitle")}
      state={isRetrying ? "loading" : section.state}
      count={section.count}
      viewAllUrl={section.view_all_url}
      onRetry={handleRetry}
      isRetrying={isRetrying}
      testId="agency-home-invite-stale"
      // OKS-953 (PRD R7 / UX Spec §5) — the agency home carries the SAME
      // five candidate segments, so a clean portfolio collapses to ~5 lines.
      // Collapsing only some of them would read as breakage in the others.
      collapseWhenEmpty
      emptyTestId="agency-home-invite-stale-empty"
      renderColdStart={() => (
        <EmptyState
          icon={MailQuestion}
          title={t("candidates.home_invite_stale.cold_start.title")}
          titleAs="h2"
          description={t(
            "candidates.home_invite_stale.agency.cold_start_description",
          )}
          data-testid="agency-home-invite-stale-cold-start"
        />
      )}
      renderEmpty={() => (
        <EmptyState
          icon={CheckCheck}
          title={t("candidates.home_invite_stale.empty.title")}
          titleAs="h2"
          description={t(
            "candidates.home_invite_stale.agency.empty_description",
          )}
          data-testid="agency-home-invite-stale-empty"
        />
      )}
    >
      {section.items.map((item) => (
        <CandidateReviewCard key={item.id} item={item} />
      ))}
    </HomeTriageSection>
  )
}
